/*
 * Copyright (c) 2020 Honeywell International Inc. All Rights Reserved.
 * Honeywell and its product names are among the trademarks and/or service marks owned by Honeywell International, Inc., or its subsidiaries.
 *
 * For patent information, see http://www.hsmpats.com.
 */

import React from 'react';
import { withRouter } from 'react-router-dom';
import { DataTable } from '@scuf/datatable';
import {
  Card,
  Grid,
  Icon,
  Tooltip,
  Input,
  Button,
  InputLabel,
  Tab,
  Loader,
  FileDrop,
} from '@scuf/common';
import { withConfigurationContext, withApplicationContext } from '../../common/HOCs';
import InlineError from '../components/InlineError';
import Routes from '../../common/routes';
import FeatureAccessTable from '../../partials/FeatureAccessTable';

class ViewConfiguration extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      form: {
        emails: [],
      },
      errors: {},
      tableDataEmails: null,
      emailInput: '',
    };
  }

  toggleLoading() {
    this.setState((prevState) => ({
      loadingSettings: !prevState.loadingSettings,
    }));
  }

  onEmailChange = (emailInput) => {
    this.setState({ emailInput });
  };

  componentDidMount() {
    this.initializeTable();
  }

  initializeTable = async () => {
    const { configurationContext } = this.props;
    let configuration = null;
    this.toggleLoading();
    try {
      configuration = await configurationContext.fetchConfiguration();
    } catch (err) {
      console.log(err);
    }
    this.convertEmailListToTableData(configuration.emails);
    const { emails } = configuration;
    this.setState({ form: { ...this.state.form, emails } });
    this.toggleLoading();
  };

  convertEmailListToTableData = (emails) => {
    const tableDataEmails = [];
    for (let i = 0; i < emails.length; i++) {
      tableDataEmails.push({ email: emails[i] });
    }
    this.setState({ tableDataEmails });
  };

  validateAndAddEmail = (email) => {
    const errors = {};
    this.setState({ errors });
    if (email === '') {
      errors.email = 'Email cannot be empty';
      this.setState({ errors });
    } else if (!this.validateEmail(email)) {
      errors.email = 'Invalid email format';
      this.setState({ errors });
    } else {
      this.addRemoveEmail(email, 'add');
    }
  };

  validateEmail = (email) => {
    const expression = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return expression.test(String(email).toLowerCase());
  };

  // Function: creates remove icon next to email in the table
  addEmailAction = (email, action) => {
    return (
      <div>
        <>
          <a onClick={() => this.addRemoveEmail(email, action)}>
            <Tooltip
              element={<Icon name="slider-controls-minus" size="large" />}
              content="Remove Email"
              position="top right"
            />
          </a>
        </>
      </div>
    );
  };

  onAccepted = async (fileList) => {
    const { configurationContext } = this.props;

    const data = new FormData();
    // get first (only) file from list
    data.append('sla', fileList[0]);
    await configurationContext.uploadSLA(data);
  };

  // Function : adds/removes email /from the email list
  addRemoveEmail = (data, action) => {
    let { tableDataEmails } = this.state;
    let { emails } = this.state.form;
    if (action === 'add') {
      tableDataEmails.push({ email: data });
      this.setState({ tableDataEmails });

      emails.push(data);
      this.setState({ form: { ...this.state.form, emails } });
      const emailInput = '';
      this.setState({ emailInput });
    } else {
      const emailStr = data.email;
      emails = this.state.form.emails.filter((_email) => {
        return _email !== emailStr;
      });
      this.setState({ form: { ...this.state.form, emails } });

      tableDataEmails = this.state.tableDataEmails.filter((_email) => {
        return _email !== data;
      });
      this.setState({ tableDataEmails });
    }
  };

  saveConfig = async () => {
    const { configurationContext, history } = this.props;
    const response = await configurationContext.saveConfiguration(this.state.form);
    if (response) {
      history.push({ pathname: Routes.Home });
    }
  };

  fileReset() {
    const { configurationContext } = this.props;
    configurationContext.resetResults(null);
  }

  render() {
    const { history } = this.props;
    const { errors, emailInput } = this.state;
    return (
      <>
        <Grid>
          <Grid.Row>
            <Grid.Column width={12}>
              <Card>
                <Card.Header title="Admin Users" />
                <Card.Content>
                  <Grid>
                    <Grid.Row>
                      <Grid.Column width={1}>
                        <InputLabel label="Email:" />
                      </Grid.Column>
                      <Grid.Column width={4}>
                        <Input
                          fluid
                          error={null}
                          value={emailInput}
                          maxLength="50"
                          placeholder="example@honeywell.com"
                          indicator="required"
                          onChange={(data) => this.onEmailChange(data)}
                        />
                      </Grid.Column>
                      <Grid.Column width={2}>
                        <a onClick={() => this.validateAndAddEmail(emailInput)}>
                          <Tooltip
                            element={<Icon name="slider-controls-plus" size="large" />}
                            content="Add Email"
                            position="top right"
                          />
                        </a>
                      </Grid.Column>
                      <Grid.Column width={4}>
                        <DataTable
                          data={this.state.tableDataEmails}
                          resizableColumns
                          columnResizeMode="fit"
                          reorderableColumns={false}
                          scrollable
                          search={false}
                          metaKeySelection={false}
                        >
                          <DataTable.Column
                            field="email"
                            header="Email"
                            sortable
                            initialWidth="25%"
                          />
                          <DataTable.Column
                            field="email"
                            header="Action"
                            sortable={false}
                            initialWidth="15%"
                            renderer={(props) => this.addEmailAction(props.rowData, 'remove')}
                          />
                        </DataTable>
                      </Grid.Column>
                      {errors.email && <InlineError text={errors.email} />}
                    </Grid.Row>
                    <Grid.Row />
                  </Grid>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={12}>
              <Card>
                <Card.Header title="Feature Access" />
                <Card.Content>
                  <Tab defaultActiveIndex={0}>
                    <Tab.Pane title="By Domain">
                      <FeatureAccessTable />
                    </Tab.Pane>
                  </Tab>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={12}>
              <Card>
                <Card.Content>
                  <Button
                    type="primary"
                    content="Save"
                    iconPosition="right"
                    onClick={this.saveConfig}
                  />
                  <Button
                    type="secondary"
                    content="Cancel"
                    iconPosition="right"
                    onClick={async () => {
                      history.push({ pathname: Routes.Home });
                    }}
                  />
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={12}>
              <Card>
                <Card.Header title="SLA Upload" />
                <Card.Content>
                  <FileDrop
                    acceptedTypes={['.html', '.htm']}
                    placeholder="Drag and drop a SLA file (.htm or .html)"
                    onAccepted={(fileList) => this.onAccepted(fileList)}
                    onReset={() => this.fileReset()}
                    multiple={false}
                  />
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </>
    );
  }
}

export default withRouter(withApplicationContext(withConfigurationContext(ViewConfiguration)));
